import {
  AssistantLauncher,
  AssistantPanel,
  Button,
  ChangeProposal,
  type ChatTurn,
  ConfirmDialog,
  ConversationSwitcher,
  InlineMessage,
  Text,
  Tooltip,
} from '@olytic/foundation';
import { History, SquarePen, Trash2 } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { objects } from '../config/objects';

import {
  askAssistant,
  type ConversationSummary,
  deleteConversation,
  type Exchange,
  listConversations,
  loadConversation,
  loadLatest,
  MAX_TITLE_LENGTH,
  renameConversation,
  requestFor,
} from './conversation';
import { applyProposal, cardChanges, type ProposalView } from './proposals';
import { suggestionsFor } from './suggestions';
import { viewingFrom } from './viewing';

/**
 * The assistant floats in the bottom-right corner on every page, for people
 * whose role may use it (AppShell): the launcher, and the panel above it while
 * open (full screen below md). Each message goes to POST /api/assistant/messages,
 * and the reply comes from Mothership's data, read-only.
 *
 * The server saves each person's conversation (assistantConversations.mjs).
 * Opening the panel loads their latest and carries on in it; "New conversation"
 * starts a fresh one (the next question opens it on the server), and "Delete
 * conversation" deletes the current one after a confirm.
 *
 * The history (Foundation's ConversationSwitcher, MOTHER-149) lists the person's
 * saved conversations, newest first, reloaded on every open. Choosing one shows its
 * messages and carries on in it (the next question names its id); Rename and Delete
 * act on any one in the list, and deleting the open one empties the panel as the
 * header's Delete does. While an answer is on its way, or a conversation is loading
 * or being deleted, the history button is shown disabled, so a reply never lands in
 * a thread the person has left.
 *
 * A reply may carry proposed changes (MOTHER-49), each shown as a ChangeProposal.
 * Approve sends it through the app's own write route as the person (proposals.ts),
 * once: the card shows approved when the route made it, or the route's error.
 * Reject (with a reason) writes nothing. Proposals are not saved with the
 * conversation, so a reload shows only the reply.
 *
 * On a record page (viewing.ts) each question also names the record it is asked
 * from (MOTHER-187), read from the page when it is sent, so "this account" follows
 * the person to another record; the empty panel says which kind of record it is.
 *
 * An empty conversation offers starter questions as chips above the composer, chosen
 * by the page (suggestions.ts, MOTHER-191). A chip is sent exactly as if typed, so it
 * names the record too. None show while a conversation loads, and Foundation drops
 * them once the thread has a turn.
 */
export function Assistant({ fetcher }: { fetcher: Fetch }): JSX.Element {
  const [open, setOpen] = useState(false);
  // The page the person is on now: a question sent from a record page names that record.
  const { pathname } = useLocation();
  const viewing = viewingFrom(pathname);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  // The open conversation as of now, for a request that settles after the person has moved on.
  const openId = useRef<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [draft, setDraft] = useState('');
  // The history: null while it loads, and the list's load failure.
  const [history, setHistory] = useState<ConversationSummary[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  // Bumped by every load of the history, so only the latest one's answer is shown.
  const historyLoad = useRef(0);
  const nextId = useRef(0);
  // Bumped by every change of conversation, so an answer or a load meant for an earlier one is dropped.
  const generation = useRef(0);
  // The generation of a past conversation the person chose that is still loading, if any.
  const selecting = useRef<number | null>(null);

  const close = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    // A dialog or menu inside the panel dismisses itself first and marks the
    // event handled, so one Escape closes one layer.
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  const waiting = exchanges.some((e) => e.reply === undefined && e.error === undefined);

  /** Shows `next` as the conversation (none: a fresh one), dropping anything still on its way for the last. */
  function show(next: { conversationId: string | null; exchanges: Exchange[] }): void {
    generation.current += 1;
    nextId.current = next.exchanges.length;
    openId.current = next.conversationId;
    setConversationId(next.conversationId);
    setExchanges(next.exchanges);
  }

  // Each time the panel opens, unless a question is still on its way: the person's latest conversation.
  useEffect(() => {
    if (!open || waiting) return;
    const at = generation.current;
    let current = true;
    setLoading(true);
    setProblem(null);
    loadLatest(fetcher).then(
      (latest) => {
        if (current && generation.current === at) show(latest);
      },
      (error: unknown) => {
        if (current) setProblem(error instanceof Error ? error.message : 'Your last conversation could not be loaded.');
      },
    ).finally(() => {
      if (current) setLoading(false);
    });
    return () => {
      // Closed before it answered: what comes back is dropped, and the panel is not left loading.
      current = false;
      setLoading(false);
    };
    // Only on opening: what a send changes must not reload it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function send(text: string): void {
    const question = text.trim();
    if (question === '' || waiting || loading) return;
    setDraft('');
    setProblem(null);
    const id = nextId.current;
    nextId.current += 1;
    const at = generation.current;
    const body = requestFor(exchanges, question, conversationId, viewing);
    setExchanges((all) => [...all, { id, question }]);
    const settle = (change: Partial<Exchange>) => {
      if (generation.current === at) setExchanges((all) => all.map((e) => (e.id === id ? { ...e, ...change } : e)));
    };
    askAssistant(fetcher, body).then(
      ({ reply, connected, conversationId: savedIn, proposals }) => {
        if (generation.current !== at) return;
        // A connected reply says where it was saved (null: nowhere); the not-connected one saves nothing.
        if (connected) {
          openId.current = savedIn;
          setConversationId(savedIn);
        }
        settle({ reply, connected, ...(proposals.length > 0 ? { proposals } : {}) });
      },
      (error: unknown) => settle({ error: error instanceof Error ? error.message : 'The assistant could not answer. Try again.' }),
    );
  }

  // Proposals being applied, by id: a second Approve while one is on its way does nothing.
  const applying = useRef(new Set<string>());

  /** Sets one proposal's view, if it is still shown. */
  function updateProposal(id: string, change: Partial<ProposalView>): void {
    setExchanges((all) =>
      all.map((e) =>
        e.proposals?.some((p) => p.proposal.id === id)
          ? { ...e, proposals: e.proposals.map((p) => (p.proposal.id === id ? { ...p, ...change } : p)) }
          : e,
      ),
    );
  }

  function approve(view: ProposalView): void {
    const { id } = view.proposal;
    if (view.state !== 'pending' || applying.current.has(id)) return;
    applying.current.add(id);
    updateProposal(id, { state: 'applying', error: undefined });
    applyProposal(fetcher, view.proposal).then(
      () => updateProposal(id, { state: 'approved' }),
      (error: unknown) =>
        updateProposal(id, { state: 'pending', error: error instanceof Error ? error.message : 'The change was not made. Try again.' }),
    ).finally(() => applying.current.delete(id));
  }

  function reject(view: ProposalView): void {
    if (view.state !== 'pending' || applying.current.has(view.proposal.id)) return;
    // Nothing is written and the model is not told; the reason is not kept.
    updateProposal(view.proposal.id, { state: 'rejected', error: undefined });
  }

  function startNew(): void {
    setProblem(null);
    show({ conversationId: null, exchanges: [] });
  }

  async function removeCurrent(): Promise<void> {
    if (conversationId === null) return;
    setDeleting(true);
    try {
      await deleteConversation(fetcher, conversationId);
      setProblem(null);
      show({ conversationId: null, exchanges: [] });
      setConfirmingDelete(false);
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'The conversation could not be deleted. Try again.');
      setConfirmingDelete(false);
    } finally {
      setDeleting(false);
    }
  }

  /** Loads the history afresh, showing the switcher's loading state until it answers. */
  function reloadHistory(): void {
    historyLoad.current += 1;
    const at = historyLoad.current;
    setHistory(null);
    setHistoryError(null);
    listConversations(fetcher).then(
      (conversations) => {
        if (historyLoad.current === at) setHistory(conversations);
      },
      (error: unknown) => {
        if (historyLoad.current !== at) return;
        setHistory([]);
        setHistoryError(error instanceof Error ? error.message : 'Your conversations could not be loaded. Try again.');
      },
    );
  }

  /** Opens a past conversation and carries on in it; anything still on its way for the last one is dropped. */
  function openConversation(chosen: ConversationSummary): void {
    if (waiting || loading || deleting) return;
    generation.current += 1;
    const at = generation.current;
    selecting.current = at;
    setLoading(true);
    setProblem(null);
    let shown = false;
    loadConversation(fetcher, chosen.id).then(
      (saved) => {
        if (generation.current !== at) return;
        show(saved);
        shown = true;
      },
      (error: unknown) => {
        if (generation.current === at) setProblem(error instanceof Error ? error.message : 'That conversation could not be loaded. Try again.');
      },
    ).finally(() => {
      if (selecting.current === at) selecting.current = null;
      // Superseded by a later change of conversation: that one owns the loading state.
      if (shown || generation.current === at) setLoading(false);
    });
  }

  /** Renames one in the history; the switcher shows a rejection's message and keeps the old title. */
  async function renameListed(chosen: ConversationSummary, title: string): Promise<void> {
    const saved = await renameConversation(fetcher, chosen.id, title);
    setHistory((all) => all?.map((c) => (c.id === chosen.id ? { ...c, title: saved } : c)) ?? all);
  }

  /** Deletes one in the history (the switcher has asked first); the open one leaves the panel empty. */
  async function deleteListed(chosen: ConversationSummary): Promise<void> {
    await deleteConversation(fetcher, chosen.id);
    setHistory((all) => all?.filter((c) => c.id !== chosen.id) ?? all);
    // Compared when the delete answers, not when it was asked: the person may have started or
    // opened another conversation meanwhile, and that one stays.
    if (chosen.id !== openId.current) return;
    setProblem(null);
    if (selecting.current !== null && selecting.current === generation.current) {
      // Another one the person chose is still loading and replaces this one when it arrives, so it
      // is not dropped: the thread is emptied in place. Nothing can be waiting for a reply here,
      // since a question cannot be sent while a conversation loads. Should the load fail, the
      // panel is left empty rather than on the deleted one.
      openId.current = null;
      nextId.current = 0;
      setConversationId(null);
      setExchanges([]);
      return;
    }
    show({ conversationId: null, exchanges: [] });
  }

  const turns = useMemo(
    () => exchanges.flatMap((e) => turnsOf(e, { approve, reject })),
    // approve and reject only call setters and read the view they are given.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [exchanges],
  );
  // The fixed "not connected" hint shows only once the server has said it has no model.
  const notConnected = exchanges.filter((e) => e.reply !== undefined).at(-1)?.connected === false;
  // Starter questions for this page, only on an empty conversation that is not loading.
  const suggestions = exchanges.length === 0 && !loading && !notConnected ? suggestionsFor(pathname) : [];

  return (
    <div className="fixed right-4 bottom-4 z-[350] flex flex-col items-end gap-2 print:hidden">
      {open ? (
        <AssistantPanel
          title="Assistant"
          mode="panel"
          showModeToggle={false}
          onModeChange={(mode) => {
            if (mode === 'closed') close();
          }}
          headerActions={
            <>
              {waiting || loading || deleting ? (
                // ConversationSwitcher has no disabled state, so its button stands in, disabled, until the answer is in.
                <Button variant="subtle" size="icon-sm" type="button" aria-label="Conversations" title="Conversations" disabled>
                  <History />
                </Button>
              ) : (
                <ConversationSwitcher
                  conversations={history}
                  activeId={conversationId}
                  onOpen={reloadHistory}
                  onSelect={openConversation}
                  onRename={renameListed}
                  onDelete={deleteListed}
                  error={historyError}
                  maxTitleLength={MAX_TITLE_LENGTH}
                />
              )}
              <Tooltip label="New conversation">
                <Button variant="subtle" size="icon-sm" aria-label="New conversation" disabled={loading || deleting} onClick={startNew}>
                  <SquarePen />
                </Button>
              </Tooltip>
              <Tooltip label="Delete conversation">
                <Button
                  variant="subtle"
                  size="icon-sm"
                  aria-label="Delete conversation"
                  disabled={conversationId === null || waiting || loading || deleting}
                  onClick={() => setConfirmingDelete(true)}
                >
                  <Trash2 />
                </Button>
              </Tooltip>
            </>
          }
          turns={turns}
          onSend={send}
          suggestions={suggestions}
          busy={waiting || loading}
          empty={
            loading ? (
              <Text size="sm" tone="muted">
                Loading your last conversation…
              </Text>
            ) : viewing === null ? (
              EMPTY_HINT
            ) : (
              // The panel lays its empty state out in a row, so the extra line is stacked under the hint.
              <div className="flex flex-col gap-2">
                {EMPTY_HINT}
                <Text size="sm" tone="muted">
                  Asking about this {objects[viewing.object].singular.toLowerCase()}.
                </Text>
              </div>
            )
          }
          composerProps={{
            as: 'form',
            value: draft,
            onValueChange: setDraft,
            label: 'Message the assistant',
            placeholder: 'Ask the assistant…',
            // A failed load or delete says so under the composer; otherwise the not-connected hint, if any.
            ...(problem !== null ? { hint: problem } : notConnected ? { hint: NOT_CONNECTED_HINT } : {}),
            voice: false,
          }}
        />
      ) : (
        <AssistantLauncher label="Assistant" onClick={() => setOpen(true)} />
      )}
      <ConfirmDialog
        open={confirmingDelete}
        onOpenChange={setConfirmingDelete}
        title="Delete this conversation?"
        description="It is removed for good, and the assistant starts a new one."
        confirmLabel="Delete conversation"
        tone="danger"
        busy={deleting}
        onConfirm={() => void removeCurrent()}
      />
    </div>
  );
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

const NOT_CONNECTED_HINT = 'Not connected to Mothership data yet';

/** The empty panel's line on every page. */
const EMPTY_HINT = (
  <Text size="sm" tone="muted">
    Ask about your accounts, deals, projects or meetings. For example: “Which opportunities close
    this month?”
  </Text>
);

interface ProposalActions {
  approve: (view: ProposalView) => void;
  reject: (view: ProposalView) => void;
}

/** An exchange as the panel shows it: the question, then the reply (with its proposals), a waiting turn, or the error. */
function turnsOf(e: Exchange, actions: ProposalActions): ChatTurn[] {
  const question: ChatTurn = { id: `u${e.id}`, role: 'user', content: e.question };
  if (e.reply !== undefined) {
    const content = e.proposals?.length ? (
      <>
        {lines(e.reply)}
        <div className="mt-3 flex flex-col gap-3">
          {e.proposals.map((view) => (
            <ProposalCard key={view.proposal.id} view={view} actions={actions} />
          ))}
        </div>
      </>
    ) : (
      lines(e.reply)
    );
    return [question, { id: `a${e.id}`, role: 'assistant', content }];
  }
  if (e.error !== undefined) return [question, { id: `a${e.id}`, role: 'assistant', content: '', error: e.error }];
  return [question, { id: `a${e.id}`, role: 'assistant', content: '', pending: true }];
}

/** One proposal: the change, Approve and Reject while it waits, and the route's error if Approve failed. */
function ProposalCard({ view, actions }: { view: ProposalView; actions: ProposalActions }): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <ChangeProposal
        title={view.proposal.summary}
        summary={view.state === 'applying' ? 'Saving…' : undefined}
        changes={cardChanges(view.proposal)}
        // Still pending while it is being applied; Approve and Reject do nothing until the route answers.
        state={view.state === 'applying' ? 'pending' : view.state}
        onApprove={() => actions.approve(view)}
        onReject={() => actions.reject(view)}
      />
      {view.error !== undefined ? (
        <InlineMessage tone="error" role="alert">
          {view.error}
        </InlineMessage>
      ) : null}
    </div>
  );
}

/** The reply's text with its line breaks kept. */
function lines(text: string): JSX.Element {
  const parts = text.split('\n');
  return (
    <>
      {parts.map((line, i) => (
        <Fragment key={i}>
          {i > 0 ? <br /> : null}
          {line}
        </Fragment>
      ))}
    </>
  );
}
